import React, { useState, useEffect } from 'react';
import { formTemplatesAPI, formSubmissionsAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import { ClipboardText, Plus, Eye, Trash, Spinner, Printer } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import SignaturePad from './SignaturePad';

const FormSubmissions = ({ entityType, entityId, userRole }) => {
  const [templates, setTemplates] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFillOpen, setIsFillOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [viewingSub, setViewingSub] = useState(null);

  useEffect(() => { loadData(); }, [entityId]);

  const loadData = async () => {
    try {
      const [templatesRes, subsRes] = await Promise.all([
        formTemplatesAPI.getAll(entityType),
        formSubmissionsAPI.getAll({ entity_type: entityType, entity_id: entityId })
      ]);
      setTemplates(templatesRes.data);
      setSubmissions(subsRes.data);
    } catch (error) { console.error('Error loading forms:', error); }
    finally { setLoading(false); }
  };

  const openFillForm = (template) => {
    setSelectedTemplate(template);
    const initial = {};
    template.fields.forEach(f => {
      if (f.field_type === 'checkbox') initial[f.id] = false;
      else if (f.field_type === 'section') return; // static, no value
      else initial[f.id] = '';
    });
    setResponses(initial);
    setIsFillOpen(true);
  };

  const handleSubmit = async () => {
    if (!selectedTemplate) return;
    // Validate required fields
    for (const field of selectedTemplate.fields) {
      if (field.required && !responses[field.id] && responses[field.id] !== false) {
        toast.error(`"${field.label}" is required`);
        return;
      }
    }
    setSubmitting(true);
    try {
      await formSubmissionsAPI.create({
        form_template_id: selectedTemplate.id,
        entity_type: entityType,
        entity_id: entityId,
        responses
      });
      toast.success('Form submitted');
      setIsFillOpen(false);
      setSelectedTemplate(null);
      setResponses({});
      const res = await formSubmissionsAPI.getAll({ entity_type: entityType, entity_id: entityId });
      setSubmissions(res.data);
    } catch (error) { toast.error('Failed to submit form'); }
    finally { setSubmitting(false); }
  };

  const handleDeleteSubmission = async (subId) => {
    try {
      await formSubmissionsAPI.delete(subId);
      toast.success('Submission deleted');
      setSubmissions(submissions.filter(s => s.id !== subId));
    } catch (error) { toast.error('Failed to delete submission'); }
  };

  const handlePrintSubmission = (sub) => {
    if (!sub) return;
    const fields = sub.fields_snapshot || [];
    const submittedAt = format(new Date(sub.created_at), 'MMMM d, yyyy h:mm a');

    const escapeHtml = (s) => String(s ?? '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

    const rows = fields.map(f => {
      if (f.field_type === 'section') {
        return `<section class="section">
          ${f.label ? `<h3>${escapeHtml(f.label)}</h3>` : ''}
          ${f.placeholder ? `<p class="para">${escapeHtml(f.placeholder).replace(/\n/g, '<br/>')}</p>` : ''}
        </section>`;
      }
      const resp = sub.responses?.[f.id];
      let valueHtml = '';
      if (f.field_type === 'signature') {
        valueHtml = (resp && typeof resp === 'string' && resp.startsWith('data:image'))
          ? `<div class="sig-box"><img src="${resp}" alt="signature"/></div>`
          : `<div class="not-filled">— Not signed —</div>`;
      } else if (typeof resp === 'boolean') {
        valueHtml = `<div class="value">${resp ? '☑ Yes' : '☐ No'}</div>`;
      } else if (resp === undefined || resp === null || resp === '') {
        valueHtml = `<div class="not-filled">— Not filled —</div>`;
      } else {
        valueHtml = `<div class="value">${escapeHtml(resp).replace(/\n/g, '<br/>')}</div>`;
      }
      return `<div class="field">
        <div class="label">${escapeHtml(f.label)}${f.required ? ' *' : ''}</div>
        ${valueHtml}
      </div>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"/>
<title>${escapeHtml(sub.form_name)} — ${escapeHtml(sub.submitted_by_name || '')}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #0f172a; margin: 0; padding: 32px 40px; background: #ffffff; }
  header { border-bottom: 2px solid #0f172a; padding-bottom: 16px; margin-bottom: 24px; }
  h1 { font-size: 22px; margin: 0 0 6px 0; font-weight: 700; }
  .meta { font-size: 12px; color: #475569; }
  section.section { margin: 20px 0 10px; }
  section.section h3 { font-size: 15px; font-weight: 700; margin: 8px 0 6px 0; color: #0f172a; border-bottom: 1px solid #cbd5e1; padding-bottom: 4px; }
  section.section p.para { font-size: 11px; color: #475569; line-height: 1.5; margin: 4px 0 0 0; white-space: pre-wrap; }
  .field { margin: 10px 0; page-break-inside: avoid; }
  .label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.12em; color: #334155; margin-bottom: 3px; }
  .value { font-size: 13px; padding: 6px 10px; border: 1px solid #e2e8f0; border-radius: 6px; background: #f8fafc; white-space: pre-wrap; }
  .not-filled { font-size: 12px; color: #94a3b8; font-style: italic; padding: 6px 10px; }
  .sig-box { border: 1px solid #cbd5e1; border-radius: 6px; padding: 6px; background: #ffffff; max-width: 360px; }
  .sig-box img { max-height: 80px; width: auto; display: block; }
  footer { margin-top: 32px; padding-top: 10px; border-top: 1px solid #cbd5e1; font-size: 10px; color: #64748b; }
  @media print {
    body { padding: 20px 24px; }
    .no-print { display: none; }
    @page { margin: 0.4in; }
  }
  .no-print { position: fixed; top: 16px; right: 16px; }
  .no-print button { padding: 10px 18px; font-size: 13px; font-weight: 600; background: #0f172a; color: #fff; border: 0; border-radius: 8px; cursor: pointer; margin-left: 8px; }
  .no-print button.secondary { background: #e2e8f0; color: #0f172a; }
</style></head>
<body>
  <div class="no-print">
    <button class="secondary" onclick="window.close()">Close</button>
    <button onclick="window.print()">Print / Save as PDF</button>
  </div>
  <header>
    <h1>${escapeHtml(sub.form_name)}</h1>
    <div class="meta">Submitted by ${escapeHtml(sub.submitted_by_name || 'Unknown')} on ${escapeHtml(submittedAt)}</div>
  </header>
  ${rows}
  <footer>Generated by CareGrid — ${escapeHtml(submittedAt)}</footer>
  <script>window.addEventListener('load', function() { setTimeout(function(){ window.print(); }, 300); });</script>
</body></html>`;

    const w = window.open('', '_blank');
    if (!w) { toast.error('Pop-up blocked — allow pop-ups and try again'); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const renderField = (field, value, onChange) => {
    switch (field.field_type) {
      case 'text':
        return <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} className="rounded-xl" data-testid={`fill-field-${field.id}`} />;
      case 'textarea':
        return <Textarea value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} rows={3} className="rounded-xl" data-testid={`fill-field-${field.id}`} />;
      case 'number':
        return <Input type="number" value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} className="rounded-xl" data-testid={`fill-field-${field.id}`} />;
      case 'date':
        return <Input type="date" value={value || ''} onChange={(e) => onChange(e.target.value)} className="rounded-xl" data-testid={`fill-field-${field.id}`} />;
      case 'select':
        return (
          <Select value={value || ''} onValueChange={onChange}>
            <SelectTrigger className="rounded-xl" data-testid={`fill-field-${field.id}`}><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent>{field.options?.map((opt, i) => <SelectItem key={i} value={opt}>{opt}</SelectItem>)}</SelectContent>
          </Select>
        );
      case 'checkbox':
        return (
          <div className="flex items-center gap-2">
            <Checkbox checked={!!value} onCheckedChange={onChange} data-testid={`fill-field-${field.id}`} />
            <span className="text-sm">{field.placeholder || 'Yes'}</span>
          </div>
        );
      case 'radio':
        return (
          <RadioGroup value={value || ''} onValueChange={onChange} data-testid={`fill-field-${field.id}`}>
            {field.options?.map((opt, i) => (
              <div key={i} className="flex items-center gap-2">
                <RadioGroupItem value={opt} id={`${field.id}-${i}`} />
                <Label htmlFor={`${field.id}-${i}`} className="text-sm font-normal">{opt}</Label>
              </div>
            ))}
          </RadioGroup>
        );
      case 'signature':
        return <SignaturePad label="" onChange={onChange} value={value} testId={`fill-field-${field.id}`} />;
      default:
        return <Input value={value || ''} onChange={(e) => onChange(e.target.value)} className="rounded-xl" />;
    }
  };

  if (loading) return <div className="flex items-center justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl font-semibold">Forms</h2>
          <p className="text-sm text-muted-foreground">Fill out and view submitted forms</p>
        </div>
        {(userRole === 'supervisor' || userRole === 'caregiver') && templates.length > 0 && (
          <Dialog>
            <DialogTrigger asChild>
              <Button className="rounded-xl gap-2" data-testid="fill-form-btn">
                <Plus weight="bold" className="w-4 h-4" /> Fill Out Form
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading">Select a Form</DialogTitle>
                <DialogDescription>Choose a form template to fill out</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 mt-4">
                {templates.map(t => (
                  <Card key={t.id} className="rounded-xl border border-border hover:border-primary/30 hover:shadow-sm transition-all cursor-pointer" onClick={() => openFillForm(t)} data-testid={`select-form-${t.id}`}>
                    <CardContent className="p-4">
                      <p className="font-medium text-sm">{t.name}</p>
                      {t.description && <p className="text-xs text-muted-foreground mt-1">{t.description}</p>}
                      <p className="text-xs text-muted-foreground mt-1">{t.fields?.length} fields</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Fill Form Dialog */}
      <Dialog open={isFillOpen} onOpenChange={(open) => { if (!open) { setIsFillOpen(false); setSelectedTemplate(null); } }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          {selectedTemplate && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">{selectedTemplate.name}</DialogTitle>
                {selectedTemplate.description && <DialogDescription>{selectedTemplate.description}</DialogDescription>}
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {selectedTemplate.fields.map(field => {
                  // "section" = static heading/paragraph — no input
                  if (field.field_type === 'section') {
                    return (
                      <div key={field.id} className="pt-2">
                        {field.label && <h4 className="font-heading text-base font-semibold mb-1">{field.label}</h4>}
                        {field.placeholder && <p className="text-sm text-muted-foreground whitespace-pre-wrap">{field.placeholder}</p>}
                      </div>
                    );
                  }
                  return (
                    <div key={field.id} className="space-y-1">
                      <Label className="text-sm">{field.label} {field.required && <span className="text-destructive">*</span>}</Label>
                      {renderField(field, responses[field.id], (val) => setResponses({ ...responses, [field.id]: val }))}
                    </div>
                  );
                })}
                <div className="flex gap-3 pt-4">
                  <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setIsFillOpen(false)}>Cancel</Button>
                  <Button className="flex-1 rounded-xl" onClick={handleSubmit} disabled={submitting} data-testid="submit-form-btn">
                    {submitting ? <><Spinner className="w-4 h-4 animate-spin mr-1" /> Submitting...</> : 'Submit Form'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* View Submission Dialog */}
      <Dialog open={!!viewingSub} onOpenChange={(open) => { if (!open) setViewingSub(null); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          {viewingSub && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <DialogTitle className="font-heading">{viewingSub.form_name}</DialogTitle>
                    <DialogDescription>Submitted by {viewingSub.submitted_by_name} on {format(new Date(viewingSub.created_at), 'MMM d, yyyy h:mm a')}</DialogDescription>
                  </div>
                  <Button variant="outline" size="sm" className="rounded-xl gap-1 shrink-0" onClick={() => handlePrintSubmission(viewingSub)} data-testid="print-submission-btn">
                    <Printer weight="duotone" className="w-4 h-4" /> Print / PDF
                  </Button>
                </div>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {(viewingSub.fields_snapshot || []).map(field => {
                  if (field.field_type === 'section') {
                    return (
                      <div key={field.id} className="pt-2">
                        {field.label && <h4 className="font-heading text-base font-semibold mb-1">{field.label}</h4>}
                        {field.placeholder && <p className="text-sm text-muted-foreground whitespace-pre-wrap">{field.placeholder}</p>}
                      </div>
                    );
                  }
                  const resp = viewingSub.responses?.[field.id];
                  const isSignature = field.field_type === 'signature';
                  return (
                    <div key={field.id} className="space-y-1">
                      <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">{field.label}</Label>
                      {isSignature ? (
                        resp && typeof resp === 'string' && resp.startsWith('data:image') ? (
                          <div className="rounded-xl border border-border bg-white p-2">
                            <img src={resp} alt="signature" className="h-24 object-contain" />
                          </div>
                        ) : (
                          <div className="p-3 rounded-xl bg-muted/30 text-sm text-muted-foreground italic">Not signed</div>
                        )
                      ) : (
                        <div className="p-3 rounded-xl bg-muted/30 text-sm">
                          {resp !== undefined && resp !== ''
                            ? (typeof resp === 'boolean' ? (resp ? 'Yes' : 'No') : String(resp))
                            : <span className="text-muted-foreground italic">Not filled</span>
                          }
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Submissions List */}
      {submissions.length === 0 ? (
        <Card className="rounded-2xl border border-border">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ClipboardText weight="duotone" className="w-16 h-16 text-muted-foreground/50 mb-4" />
            <h3 className="font-heading text-lg font-medium">No form submissions yet</h3>
            <p className="text-muted-foreground text-sm mt-1">
              {templates.length > 0 ? 'Click "Fill Out Form" to submit one' : 'No form templates available yet'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {submissions.map(sub => (
            <Card key={sub.id} className="rounded-2xl border border-border hover:border-border/80 hover:shadow-sm transition-all duration-200" data-testid={`submission-card-${sub.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 min-w-0">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                      <ClipboardText weight="duotone" className="w-6 h-6 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm">{sub.form_name}</p>
                      <p className="text-xs text-muted-foreground">
                        Submitted by {sub.submitted_by_name} &middot; {format(new Date(sub.created_at), 'MMM d, yyyy h:mm a')}
                      </p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {Object.entries(sub.responses || {}).slice(0, 3).map(([key, val]) => {
                          const field = sub.fields_snapshot?.find(f => f.id === key);
                          if (!field || val === '' || val === false) return null;
                          return <Badge key={key} variant="secondary" className="text-[10px] rounded-full">{field.label}: {typeof val === 'boolean' ? 'Yes' : String(val).substring(0, 20)}</Badge>;
                        })}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" className="rounded-xl" onClick={() => setViewingSub(sub)} data-testid={`view-submission-${sub.id}`} title="View">
                      <Eye weight="bold" className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="rounded-xl" onClick={() => handlePrintSubmission(sub)} data-testid={`print-submission-${sub.id}`} title="Print / Save as PDF">
                      <Printer weight="bold" className="w-4 h-4" />
                    </Button>
                    {userRole === 'supervisor' && (
                      <Button variant="ghost" size="sm" className="rounded-xl text-destructive hover:text-destructive" onClick={() => handleDeleteSubmission(sub.id)} data-testid={`delete-submission-${sub.id}`} title="Delete">
                        <Trash weight="bold" className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default FormSubmissions;
